import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { addProduct, getCategories } from "../utils/api";

export default function AddItem({ token, currentUser }) {
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [loadingCats, setLoadingCats] = useState(true);

  const [name, setName] = useState("");
  const [productCategoryId, setProductCategoryId] = useState("");
  const [price, setPrice] = useState("");
  const [itemCondition, setItemCondition] = useState("New");
  const [status, setStatus] = useState("available");
  const [description, setDescription] = useState("");
  const [imageFile, setImageFile] = useState(null);

  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadCats() {
      try {
        const rows = await getCategories();
        const list = Array.isArray(rows) ? rows : [];
        if (cancelled) return;

        setCategories(list);
        if (!productCategoryId && list.length > 0) {
          setProductCategoryId(String(list[0].id));
        }
      } catch {
        if (!cancelled) setCategories([]);
      } finally {
        if (!cancelled) setLoadingCats(false);
      }
    }

    loadCats();
    return () => {
      cancelled = true;
    };
  }, [productCategoryId]);

  async function onSubmit(e) {
    e.preventDefault();
    setMsg("");

    if (!token) return setMsg("Login first.");
    if (!productCategoryId) return setMsg("Please select a category.");

    try {
      setSaving(true);

      const fd = new FormData();
      fd.append("name", name);
      fd.append("product_category_id", productCategoryId);
      fd.append("price", String(price));
      fd.append("item_condition", itemCondition);
      fd.append("status", status);
      fd.append("description", description);
      if (imageFile) fd.append("image", imageFile);

      await addProduct(token, fd);
      navigate("/my-listings");
    } catch (err) {
      setMsg(err?.message || "Failed to add item");
    } finally {
      setSaving(false);
    }
  }

  if (!currentUser) {
    return (
      <div className="container pagePadBottom" style={{ maxWidth: 720 }}>
        <div className="card">
          <div style={{ fontWeight: 900, fontSize: 20 }}>Add Item</div>
          <div className="muted" style={{ marginTop: 6 }}>
            Login first.
          </div>
          <div className="row" style={{ marginTop: 12 }}>
            <Link className="btnPrimary" to="/login">
              Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const role = String(currentUser?.role || "").toLowerCase();
  if (role !== "seller" && role !== "admin") {
    return (
      <div className="container pagePadBottom" style={{ maxWidth: 720 }}>
        <div className="card">
          <div style={{ fontWeight: 900, fontSize: 20 }}>Add Item</div>
          <div className="muted" style={{ marginTop: 6 }}>
            Only sellers can create listings.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container pagePadBottom" style={{ maxWidth: 820 }}>
      <div
        className="row"
        style={{
          justifyContent: "space-between",
          alignItems: "flex-end",
          flexWrap: "wrap",
        }}
      >
        <div>
          <div style={{ fontWeight: 900, fontSize: 22 }}>Create listing</div>
          <div className="muted" style={{ marginTop: 6, fontSize: 13 }}>
            Advertise your item
          </div>
        </div>
        <Link className="btnSecondary" to="/my-listings">
          Back
        </Link>
      </div>

      <div className="card" style={{ marginTop: 12 }}>
        <form onSubmit={onSubmit} style={{ display: "grid", gap: 12 }}>
          <div>
            <div className="label">Item name</div>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div>
            <div className="label">Category</div>
            <select
              className="input"
              value={productCategoryId}
              onChange={(e) => setProductCategoryId(e.target.value)}
              disabled={loadingCats || categories.length === 0}
            >
              {categories.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.title ?? c.label ?? "Category"}
                </option>
              ))}
            </select>
          </div>

          <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div className="label">Price ($)</div>
              <input
                className="input"
                type="number"
                min="0"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                required
              />
            </div>

            <div style={{ flex: 1, minWidth: 220 }}>
              <div className="label">Condition</div>
              <select
                className="input"
                value={itemCondition}
                onChange={(e) => setItemCondition(e.target.value)}
              >
                <option>New</option>
                <option>Like New</option>
                <option>Used</option>
              </select>
            </div>
          </div>

          <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div className="label">Status</div>
              <select
                className="input"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="available">available</option>
                <option value="reserved">reserved</option>
                <option value="sold">sold</option>
                <option value="removed">removed</option>
              </select>
            </div>

            <div style={{ flex: 1, minWidth: 220 }}>
              <div className="label">Image (optional)</div>
              <input
                className="input"
                type="file"
                accept="image/*"
                onChange={(e) => setImageFile(e.target.files?.[0] || null)}
              />
            </div>
          </div>

          <div>
            <div className="label">Description</div>
            <textarea
              className="input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              placeholder="Short description…"
            />
          </div>

          {msg ? (
            <div className="card" style={{ borderLeft: "4px solid #e11d48" }}>
              <div style={{ fontWeight: 900 }}>{msg}</div>
            </div>
          ) : null}

          <button
            className="btnPrimary"
            disabled={saving}
            style={{ justifyContent: "center" }}
          >
            {saving ? "Saving…" : "Create listing"}
          </button>
        </form>
      </div>
    </div>
  );
}
